import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  filterUiTestNavigatorRoutes,
  UI_TEST_NAVIGATOR_CATEGORIES
} from '@/constants/uiTestNavigatorRoutes'
import { areTestRoutesEnabled } from '@/routes/testRoutes'
import { useAuth } from '@/context/AuthContext'
import { ROLES } from '@/config/roles'
import './UiTestNavigator.css'

const isTestPath = (pathname) =>
  pathname === '/test' || pathname.startsWith('/test/')

const UiTestNavigator = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser, userRole } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searchRef = useRef(null)

  const enabled = areTestRoutesEnabled()
  const isAdmin = Boolean(currentUser) && userRole === ROLES.ADMIN
  const onTestRoute = isTestPath(location.pathname)
  const filteredRoutes = useMemo(
    () => filterUiTestNavigatorRoutes(query),
    [query]
  )

  const groupedRoutes = useMemo(() => {
    const groups = UI_TEST_NAVIGATOR_CATEGORIES.map((category) => ({
      category,
      routes: filteredRoutes.filter((route) => route.category === category)
    })).filter((group) => group.routes.length > 0)

    const known = new Set(UI_TEST_NAVIGATOR_CATEGORIES)
    const other = filteredRoutes.filter((route) => !known.has(route.category))
    if (other.length) {
      groups.push({ category: 'Other', routes: other })
    }
    return groups
  }, [filteredRoutes])

  const currentPath = `${location.pathname}${location.search || ''}`

  useEffect(() => {
    if (!onTestRoute && isOpen) {
      setIsOpen(false)
      setQuery('')
    }
  }, [onTestRoute, isOpen])

  useEffect(() => {
    if (!isOpen) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    const timer = window.setTimeout(() => {
      searchRef.current?.focus()
    }, 0)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.clearTimeout(timer)
    }
  }, [isOpen])

  if (!enabled || !isAdmin || !onTestRoute) return null

  const goToRoute = (path) => {
    setIsOpen(false)
    setQuery('')
    navigate(path)
  }

  return (
    <>
      <button
        type='button'
        className='ui-test-navigator-fab'
        aria-haspopup='dialog'
        aria-expanded={isOpen}
        aria-controls='ui-test-navigator-modal'
        onClick={() => setIsOpen(true)}
      >
        UI - Test - Navigator
      </button>

      {isOpen && (
        <div
          className='ui-test-navigator-overlay'
          onClick={() => setIsOpen(false)}
        >
          <div
            id='ui-test-navigator-modal'
            className='ui-test-navigator-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='ui-test-navigator-title'
            onClick={(event) => event.stopPropagation()}
          >
            <div className='ui-test-navigator-header'>
              <div>
                <h2 id='ui-test-navigator-title'>UI - Test - Navigator</h2>
                <p>
                  Search and open any UI test/preview page. Dev and staging
                  only.
                </p>
              </div>
              <button
                type='button'
                className='ui-test-navigator-close'
                aria-label='Close UI test navigator'
                onClick={() => setIsOpen(false)}
              >
                Close
              </button>
            </div>

            <div className='ui-test-navigator-search-wrap'>
              <input
                ref={searchRef}
                type='search'
                className='ui-test-navigator-search'
                placeholder='Search page name or route…'
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label='Search UI test pages'
              />
              <p className='ui-test-navigator-count'>
                {filteredRoutes.length} page
                {filteredRoutes.length === 1 ? '' : 's'}
              </p>
            </div>

            <div className='ui-test-navigator-list'>
              {groupedRoutes.length === 0 ? (
                <p className='ui-test-navigator-empty'>No matching pages.</p>
              ) : (
                groupedRoutes.map((group) => (
                  <section
                    key={group.category}
                    className='ui-test-navigator-group'
                  >
                    <h3>{group.category}</h3>
                    <div className='ui-test-navigator-group-items'>
                      {group.routes.map((route) => {
                        const isActive = currentPath === route.path
                        return (
                          <button
                            key={route.path}
                            type='button'
                            className={`ui-test-navigator-item${
                              isActive ? ' ui-test-navigator-item--active' : ''
                            }`}
                            onClick={() => goToRoute(route.path)}
                          >
                            <span className='ui-test-navigator-item-name'>
                              {route.name}
                            </span>
                            <code className='ui-test-navigator-item-path'>
                              {route.path}
                            </code>
                          </button>
                        )
                      })}
                    </div>
                  </section>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default UiTestNavigator
